/**
 * Relays, from the planner's World: the hands it has taken in, the relays
 * it has laid, and for one relay its standing, its windows and its acts.
 *
 * The act buttons are the relay's `possible`, as the API reads it off the
 * record: the console never decides on its own what may be done next.
 */

import type { Api, LegSpec, Moment, Relay, RelayAct, RelayVerb, World } from './api';
import { Notices, field, h, mount, nameBox, short, standingBadge } from './dom';
import { globalName, worldName } from './names';
import { atLabel, atOf, runs, today } from './grid';

const VERBS: readonly RelayVerb[] = ['ask', 'sync', 'withdraw', 'delegate'];

/** Draw the relays tab; returns how to re-read it. */
export const renderRelays = (el: HTMLElement, api: Api, world: World, notices: Notices, relay?: string): (() => Promise<void>) =>
  relay ? relayDetail(el, api, world, notices, relay) : relayIndex(el, api, world, notices);

// -- hands and relays --------------------------------------------------------------------------

const relayIndex = (el: HTMLElement, api: Api, world: World, notices: Notices) => {
  const hands = h('div', {});
  const relays = h('div', {});
  const handName = h('input', { type: 'text', list: 'world-names', placeholder: 'maker', required: true, autocapitalize: 'none' });
  const handBox = nameBox(handName, globalName);
  const names = h('datalist', { id: 'world-names' });
  const relayName = h('input', { type: 'text', required: true, placeholder: 'order-1', autocapitalize: 'none' });
  const relayBox = nameBox(relayName, worldName);
  const legRows = h('div', { class: 'legs' });
  let handList: string[] = [];
  const legs: { hand: string; length: number; gapAfter: number; after: string }[] = [{ hand: '', length: 1, gapAfter: 0, after: '' }];

  const drawLegs = () => {
    mount(legRows,
      h('div', { class: 'leg-row head' }, h('span', {}, '#'), h('span', {}, 'hand'), h('span', {}, 'length'), h('span', {}, 'gap after'), h('span', {}, 'after'), h('span', {}, '')),
      ...legs.map((leg, i) => {
        const hand = h('select', { 'aria-label': `leg ${i} hand` }, ...handList.map((name) => h('option', { value: name, selected: name === leg.hand }, name)));
        if (!leg.hand && handList[0]) leg.hand = handList[0];
        hand.addEventListener('change', () => { leg.hand = hand.value; });
        const num = (key: 'length' | 'gapAfter', min: number) => {
          const input = h('input', { type: 'number', min, value: leg[key], 'aria-label': `leg ${i} ${key}` });
          input.addEventListener('change', () => { leg[key] = Math.max(min, Number(input.value) || min); });
          return input;
        };
        const after = h('input', { type: 'text', value: leg.after, placeholder: i === 0 ? '' : 'prev', class: 'mono', 'aria-label': `leg ${i} after`, title: 'legs this one waits for, like "0,1"; empty: the one before; "-": none' });
        after.addEventListener('change', () => { leg.after = after.value; });
        return h('div', { class: 'leg-row' },
          h('span', { class: 'mono muted' }, i), hand, num('length', 1), num('gapAfter', 0), after,
          h('button', { type: 'button', class: 'ghost small', 'aria-label': `remove leg ${i}`, onclick: () => { legs.splice(i, 1); drawLegs(); } }, '×'));
      }),
      h('button', { type: 'button', class: 'add', onclick: () => { legs.push({ hand: handList[0] ?? '', length: 1, gapAfter: 0, after: '' }); drawLegs(); } }, '+ leg'));
  };

  const takeIn = async (e: Event) => {
    e.preventDefault();
    const hand = handBox.read();
    if (!hand) return handName.focus();
    if (!notices.take(await api.takeIn(world.name, hand))) return;
    notices.ok(`Took ${hand} in: its busy time is copied here for planning`);
    handName.value = '';
    await load();
  };

  const lay = async (e: Event) => {
    e.preventDefault();
    const spec: LegSpec[] = legs.map((l) => {
      const after = l.after.trim();
      return {
        hand: l.hand, length: l.length, ...(l.gapAfter ? { gapAfter: l.gapAfter } : {}),
        // Empty keeps the API's default (the leg before); "-" runs from the start, side by side.
        ...(after === '' ? {} : { after: after === '-' ? [] : after.split(',').map((s) => Number(s.trim())) }),
      };
    });
    const named = relayBox.read();
    if (!named) return relayName.focus();
    const made = notices.take(await api.layRelay(world.name, named, spec));
    if (!made) return;
    location.hash = `#/w/${encodeURIComponent(world.name)}/relays/${encodeURIComponent(made.name)}`;
  };

  mount(el, h('div', { class: 'columns' },
    h('div', { class: 'stack' },
      h('div', { class: 'card' }, h('h3', {}, 'Relays laid here'), relays),
      h('div', { class: 'card' }, h('h3', {}, 'Hands taken in'), hands,
        h('form', { class: 'inline-form', onsubmit: takeIn }, handName, names, h('button', { type: 'submit' }, 'Take in'), handBox.hint),
        h('p', { class: 'hint' }, 'A hand is another World in this workspace. Taking it in copies its busy time here; take it in again to refresh.'))),
    h('form', { class: 'card', onsubmit: lay },
      h('h3', {}, 'Lay a relay'),
      field('Name', relayName, undefined, relayBox.hint),
      legRows,
      h('p', { class: 'hint' }, 'Length and gap are in slots. "after" lists earlier legs to wait for (0-based); empty waits for the leg before, "-" starts with the first.'),
      h('button', { class: 'primary', type: 'submit' }, 'Lay'))));

  const load = async () => {
    const [handsAnswer, relaysAnswer, worldsAnswer] = await Promise.all([api.hands(world.name), api.relays(world.name), api.worlds()]);
    const hs = notices.take(handsAnswer);
    const rs = notices.take(relaysAnswer);
    if (worldsAnswer.ok) mount(names, ...worldsAnswer.value.worlds.filter((w) => w.name !== world.name).map((w) => h('option', { value: w.name })));
    if (hs) {
      // The API names a hand globally (workspace/world); a relay's leg takes the bare name in this workspace too.
      handList = hs.hands.map((x) => x.hand);
      mount(hands, hs.hands.length === 0 ? h('p', { class: 'hint' }, 'None yet.') : h('ul', { class: 'plain' },
        ...hs.hands.map((x) => h('li', {}, h('span', { class: 'mono' }, x.hand), h('span', { class: 'mono muted', title: x.root }, ` root ${short(x.root)}`),
          h('button', { class: 'ghost small', onclick: async () => { if (notices.take(await api.takeIn(world.name, x.hand))) { notices.ok(`Refreshed ${x.hand}`); await load(); } } }, 'refresh')))));
      drawLegs();
    }
    if (rs) {
      mount(relays, rs.relays.length === 0 ? h('p', { class: 'hint' }, 'None yet. Take hands in, then lay one.') : h('table', {},
        h('thead', {}, h('tr', {}, h('th', {}, 'Relay'), h('th', {}, 'Standing'), h('th', {}, 'Legs'), h('th', {}, 'Possible'))),
        h('tbody', {}, ...rs.relays.map((r) => h('tr', {},
          h('td', {}, h('a', { href: `#/w/${encodeURIComponent(world.name)}/relays/${encodeURIComponent(r.name)}` }, r.name)),
          h('td', {}, standingBadge(r.standing)),
          h('td', {}, h('span', { class: 'strip' }, ...r.legs.map((l) => h('i', { class: `s-${l.standing}`, title: `leg ${l.leg} · ${l.hand} · ${l.standing}` })))),
          h('td', { class: 'mono muted' }, r.possible.join(' · ')))))));
    }
  };
  drawLegs();
  return load;
};

// -- one relay --------------------------------------------------------------------------------

const relayDetail = (el: HTMLElement, api: Api, world: World, notices: Notices, name: string) => {
  const summary = h('div', {});
  const legsView = h('div', {});
  const buttons = h('div', { class: 'acts' });
  const told = h('div', {});
  const windowsList = h('div', { class: 'starts' });
  const minute = world.preset === 'temporal-minute';

  const askDate = h('input', { type: 'date', value: today() });
  const askHour = h('input', { type: 'number', min: 0, max: 23, value: 9, class: 'short', hidden: !minute });
  const askSlot = h('input', { type: 'number', min: 0, max: world.leafSlotBits - 1, value: minute ? 0 : 9, class: 'short' });
  const winDate = h('input', { type: 'date', value: today() });
  const winHour = h('input', { type: 'number', min: 0, max: 23, value: 9, class: 'short', hidden: !minute });
  const winPositions = h('input', { type: 'number', min: 1, max: 512, value: 7, class: 'short' });
  const delegateLeg = h('select', { 'aria-label': 'leg to delegate' });
  const delegateTo = h('select', { 'aria-label': 'hand to delegate to' });

  const start = (): Moment => ({ date: askDate.value, ...(minute ? { hour: Number(askHour.value) } : {}), slot: Number(askSlot.value) });

  const act = async (verb: RelayVerb) => {
    const body: RelayAct = verb === 'ask' ? { act: 'ask', start: start() }
      : verb === 'delegate' ? { act: 'delegate', leg: Number(delegateLeg.value), to: delegateTo.value }
        : { act: verb };
    const answer = await api.act(world.name, name, body);
    if (!answer.ok) {
      notices.show(answer.problem);
      return;
    }
    notices.ok(`${verb}: the relay stands ${answer.value.standing}`);
    mount(told, h('details', { class: 'told' }, h('summary', {}, `What ${verb} changed`), h('pre', {}, JSON.stringify(answer.value.told ?? null, null, 2))));
    draw(answer.value);
  };

  const findWindows = async (e: Event) => {
    e.preventDefault();
    const at = { date: winDate.value, ...(minute ? { hour: Number(winHour.value) } : {}) };
    const answer = notices.take(await api.windows(world.name, name, at, Number(winPositions.value) || 1));
    if (!answer) return;
    mount(windowsList, answer.starts.length === 0
      ? h('p', { class: 'hint' }, `No start fits.${answer.blockedAt ? ` Blocked at ${JSON.stringify(answer.blockedAt)}.` : ''}`)
      : h('div', { class: 'chips' }, ...answer.starts.slice(0, 200).map((s) => {
        const at_ = atOf(s.parametric, s.coordinates);
        return h('button', {
          class: 'chip mono',
          title: 'use as the ask\'s start',
          onclick: () => {
            askDate.value = at_.date;
            if (at_.hour !== undefined) askHour.value = String(at_.hour);
            askSlot.value = String(s.slot);
          },
        }, `${atLabel(at_)} @${s.slot}`);
      })));
  };

  const draw = (r: Relay) => {
    mount(summary, h('div', { class: 'relay-summary' },
      h('span', {}, 'Standing '), standingBadge(r.standing),
      h('span', { class: 'mono muted', title: r.head }, ` planner head ${short(r.head)}`),
      r.shortfalls.length ? h('p', { class: 'hint warn' }, `Shortfalls: ${r.shortfalls.map((s) => `leg ${s.leg} (${s.hand}) must move ${s.by} later`).join('; ')}`) : null));

    mount(legsView, h('table', {},
      h('thead', {}, h('tr', {}, ...['Leg', 'Hand', 'Length', 'Gap', 'After', 'Standing', 'Where', 'Because'].map((t) => h('th', {}, t)))),
      h('tbody', {}, ...r.legs.map((l) => h('tr', {},
        h('td', { class: 'mono' }, l.leg),
        h('td', {}, l.hand),
        h('td', { class: 'mono' }, l.length),
        h('td', { class: 'mono' }, l.gapAfter ?? 0),
        h('td', { class: 'mono' }, l.after ? (l.after.length ? l.after.join(',') : '—') : l.leg > 0 ? String(l.leg - 1) : '—'),
        h('td', {}, standingBadge(l.standing)),
        h('td', { class: 'mono' }, l.pieces.map((p) => `${atLabel(atOf(p.parametric, p.coordinates))} ${runs(p.slots)}`).join('; ') || '—'),
        h('td', { class: 'muted' }, l.because ? `${l.because.by}${l.because.conflictsWith?.length ? ` (${l.because.conflictsWith.join(', ')})` : ''}` : ''))))));

    // A lane per leg over the slots of the first position any leg sits at.
    const first = r.legs.flatMap((l) => l.pieces)[0];
    if (first) {
      const key = `${first.parametric}:${first.coordinates.join(',')}`;
      legsView.append(h('div', { class: 'lanes' },
        h('p', { class: 'hint' }, `At ${atLabel(atOf(first.parametric, first.coordinates))}:`),
        ...r.legs.map((l) => {
          const here = new Set(l.pieces.filter((p) => `${p.parametric}:${p.coordinates.join(',')}` === key).flatMap((p) => p.slots));
          return h('div', { class: 'lane' }, h('span', { class: 'mono muted' }, `${l.leg} ${l.hand}`),
            h('span', { class: `lane-cells bits-${world.leafSlotBits}` }, ...Array.from({ length: world.leafSlotBits }, (_, s) =>
              h('i', { class: here.has(s) ? `on s-${l.standing}` : '', title: `slot ${s}` }))));
        })));
    }

    mount(delegateLeg, ...r.legs.map((l) => h('option', { value: l.leg }, `leg ${l.leg} (${l.hand})`)));
    mount(buttons, ...VERBS.map((verb) => h('button', {
      class: verb === 'ask' ? 'primary' : verb === 'withdraw' ? 'danger' : '',
      disabled: !r.possible.includes(verb),
      title: r.possible.includes(verb) ? '' : 'the record does not allow this now',
      onclick: () => void act(verb),
    }, verb)));
  };

  mount(el,
    h('p', {}, h('a', { href: `#/w/${encodeURIComponent(world.name)}/relays`, class: 'muted' }, '← Relays'), ' ', h('strong', {}, name)),
    h('div', { class: 'card' }, summary, legsView),
    h('div', { class: 'columns' },
      h('div', { class: 'card' },
        h('h3', {}, 'Act'),
        h('p', { class: 'hint' }, 'Only the acts the record allows now are enabled. Ask sends one ask per leg; nothing is held until each hand says yes.'),
        h('div', { class: 'inline' }, h('span', {}, 'Start'), askDate, askHour, h('span', {}, 'slot'), askSlot),
        h('div', { class: 'inline' }, h('span', {}, 'Delegate'), delegateLeg, h('span', {}, 'to'), delegateTo),
        buttons, told),
      h('form', { class: 'card', onsubmit: findWindows },
        h('h3', {}, 'Windows'),
        h('p', { class: 'hint' }, 'Every start where all legs fit, from one pass over every hand\'s free slots. Click one to use it.'),
        h('div', { class: 'inline' }, h('span', {}, 'From'), winDate, winHour, h('span', {}, 'for'), winPositions, h('span', {}, minute ? 'hours' : 'days')),
        h('button', { type: 'submit' }, 'Find windows'),
        windowsList)));

  return async () => {
    const [relayAnswer, hands] = await Promise.all([api.relay(world.name, name), api.hands(world.name)]);
    if (hands.ok) mount(delegateTo, ...hands.value.hands.map((x) => h('option', { value: x.hand }, x.hand)));
    const r = notices.take(relayAnswer);
    if (r) draw(r);
  };
};
